import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiZap } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Card, DataTable, Button, Badge, ConfirmDialog, Toggle, Modal, ImageUpload } from '../../components/common';
import { IMAGE_SIZE_PRESETS } from '../../utils/imageCompressor';
import { teamMemberService, teamMemberImageUrl } from '../../services/teamMemberService';
import ReviewFormInput from '../reviews/ReviewFormInput';
import { useModal, useForm } from '../../hooks';
import { validators } from '../../utils/validators';
import './TeamMembersPage.css';

const TeamMembersPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const formModal = useModal();
  const deleteModal = useModal();
  const [saving, setSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Mirrors the API: only name and designation are required.
  const validationRules = {
    name: [validators.required, validators.minLength(2)],
    designation: [validators.required],
  };

  const form = useForm({
    name: '',
    designation: '',
    image: null,
    duration: '',
    experience: '',
    isActive: true,
  }, validationRules);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await teamMemberService.getAll();
      setMembers(response.data.members);
    } catch (error) {
      toast.error('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // The endpoint has no `search` param and DataTable only reports the typed
  // value, so the filtering happens here. Nothing is requested, so no debounce.
  const visibleMembers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return members;
    return members.filter((m) =>
      `${m.name} ${m.designation}`.toLowerCase().includes(term)
    );
  }, [members, search]);

  const handleOpenForm = (member = null) => {
    if (member) {
      form.setValues({
        id: member.id,
        name: member.name,
        designation: member.designation,
        // A string URL rather than a File, so buildFormData skips it and the
        // existing photo survives a save that does not touch it.
        image: teamMemberImageUrl(member.image),
        duration: member.duration || '',
        experience: member.experience || '',
        isActive: member.isActive,
      });
    } else {
      form.reset();
    }
    formModal.open(member);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.validate()) {
      toast.error('Please fix the form errors');
      return;
    }

    setSaving(true);
    try {
      // ImageUpload hands back { file, preview } for a new upload; an untouched
      // existing photo is still the string URL set above.
      const payload = {
        name: form.values.name,
        designation: form.values.designation,
        duration: form.values.duration,
        experience: form.values.experience,
        isActive: form.values.isActive,
        image: form.values.image?.file || form.values.image,
      };

      if (formModal.data) {
        await teamMemberService.update(formModal.data.id, payload);
        toast.success('Team member updated successfully');
      } else {
        await teamMemberService.create(payload);
        toast.success('Team member added successfully');
      }

      formModal.close();
      fetchMembers();
    } catch (error) {
      toast.error('Failed to save team member');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await teamMemberService.toggleStatus(id);
      setMembers(prev => prev.map(item =>
        item.id === id ? { ...item, isActive: !item.isActive } : item
      ));
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await teamMemberService.delete(deleteModal.data);
      toast.success('Team member deleted successfully');
      fetchMembers();
    } catch (error) {
      toast.error('Failed to delete team member');
    } finally {
      setDeleteLoading(false);
      deleteModal.close();
    }
  };

  const fillDemoData = () => {
    form.setValues({
      ...form.values,
      name: 'Rajesh Kumar',
      designation: 'Lead Solar Architect',
      duration: 'Jan 2020 - Present',
      experience: '8 years',
      isActive: true,
    });
    toast.info('Demo data filled');
  };

  const columns = [
    {
      header: 'Member',
      // accessor kept alongside render so the CSV export is not blank.
      accessor: 'name',
      render: (row) => (
        <div className="member-cell">
          {row.image ? (
            <img src={teamMemberImageUrl(row.image)} alt={row.name} className="member-photo" />
          ) : (
            <span className="member-initials">{row.name.charAt(0).toUpperCase()}</span>
          )}
          <div className="member-identity">
            <span className="member-name">{row.name}</span>
            <span className="member-designation">{row.designation}</span>
          </div>
        </div>
      )
    },
    {
      header: 'Duration',
      accessor: 'duration',
      // A dash, not a blank cell — in a dense table blank reads as "broken".
      render: (row) => row.duration || '-'
    },
    {
      header: 'Experience',
      accessor: 'experience',
      render: (row) => row.experience || '-'
    },
    {
      header: 'Status',
      width: '100px',
      render: (row) => (
        <Toggle
          name={`status-${row.id}`}
          checked={row.isActive}
          onChange={() => handleToggleStatus(row.id)}
          size="small"
        />
      )
    },
    {
      header: 'State',
      width: '100px',
      render: (row) => (
        <Badge variant={row.isActive ? 'success' : 'warning'}>
          {row.isActive ? 'Active' : 'Inactive'}
        </Badge>
      )
    },
    {
      header: 'Actions',
      width: '100px',
      align: 'center',
      render: (row) => (
        <div className="action-buttons">
          <button
            className="action-btn edit"
            title="Edit"
            onClick={() => handleOpenForm(row)}
          >
            <FiEdit2 />
          </button>
          <button
            className="action-btn delete"
            title="Delete"
            onClick={() => deleteModal.open(row.id)}
          >
            <FiTrash2 />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="team-members-page">
      <Card
        title="Our Team"
        subtitle={`${members.length} team members`}
        action={
          <Button icon={<FiPlus />} onClick={() => handleOpenForm()}>
            Add Member
          </Button>
        }
      >
        <DataTable
          columns={columns}
          data={visibleMembers}
          loading={loading}
          searchable
          searchPlaceholder="Search team members..."
          searchValue={search}
          onSearch={setSearch}
          exportable
          exportFilename="team-members-export"
          emptyMessage="No team members found"
        />
      </Card>

      {/* Form Modal */}
      <Modal
        isOpen={formModal.isOpen}
        onClose={formModal.close}
        title={formModal.data ? 'Edit Team Member' : 'Add Team Member'}
        size="medium"
        footer={
          <>
            <Button variant="ghost" onClick={formModal.close}>
              Cancel
            </Button>
            <Button variant="secondary" icon={<FiZap />} onClick={fillDemoData} type="button">
              Fill Demo Data
            </Button>
            <Button onClick={handleSubmit} loading={saving}>
              {formModal.data ? 'Update' : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="team-member-form">
          <ReviewFormInput
            name="name"
            label="Name"
            placeholder="Enter member name"
            value={form.values.name}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={form.errors.name}
            touched={form.touched.name}
            required
          />
          <ReviewFormInput
            name="designation"
            label="Designation"
            placeholder="e.g. Lead Solar Architect"
            value={form.values.designation}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            error={form.errors.designation}
            touched={form.touched.designation}
            required
          />
          {/* Free text on purpose — the client writes what should appear on the
              card. The placeholders are the only cue about the format. */}
          <ReviewFormInput
            name="duration"
            label="Duration (optional)"
            placeholder="e.g. Jan 2020 - Present"
            value={form.values.duration}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />
          <ReviewFormInput
            name="experience"
            label="Experience (optional)"
            placeholder="e.g. 8 years"
            value={form.values.experience}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />
          <ImageUpload
            value={form.values.image}
            onChange={(image) => form.setFieldValue('image', image)}
            label="Photo (optional)"
            {...IMAGE_SIZE_PRESETS.small}
          />
          <Toggle
            name="isActive"
            checked={form.values.isActive}
            onChange={(e) => form.setFieldValue('isActive', e.target.checked)}
            label="Active"
          />
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDelete}
        title="Delete Team Member"
        message="Are you sure you want to delete this team member? This action cannot be undone."
        confirmText="Delete"
        loading={deleteLoading}
      />
    </div>
  );
};

export default TeamMembersPage;
